import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// Clave para el caché de React Query
const UNIFIED_ADMIN_METRICS_KEY = 'unified-admin-metrics';

// Tipos para las métricas unificadas
export interface FunnelMetrics {
  total_visits: number;
  total_registrations: number;
  total_unconfirmed_emails: number;
  total_confirmed_emails: number;
  total_in_trial: number;
  total_in_grace_period: number;
  total_premium_active: number;
  registration_rate: number;
  email_confirmation_rate: number;
  trial_conversion_rate: number;
  grace_period_rate: number;
  premium_conversion_rate: number;
  visits_to_registration_rate: number;
  visits_to_confirmed_email_rate: number;
  visits_to_trial_rate: number;
  visits_to_grace_period_rate: number;
  visits_to_premium_rate: number;
}

export interface MainStats {
  active_users: number;
  mrr: number;
  churn_rate: number;
  conversion_rate: number;
}

export interface SubscriptionMetrics {
  average_ltv: number;
  total_active_subscribers: number;
  average_subscription_length: number;
}

export interface MetricsMetadata {
  generated_at: string;
  date_range: {
    start_date: string | null;
    end_date: string | null;
  };
}

export interface UnifiedAdminMetrics {
  funnel: FunnelMetrics;
  main_stats: MainStats;
  subscription_metrics: SubscriptionMetrics;
  metadata: MetricsMetadata;
}

interface UseUnifiedAdminMetricsOptions {
  startDate?: Date;
  endDate?: Date;
  staleTime?: number; // Tiempo en ms antes de considerar los datos obsoletos
  cacheTime?: number; // Tiempo en ms para mantener los datos en caché
}

/**
 * Hook para obtener métricas unificadas del dashboard administrativo
 * Utiliza React Query para implementar caché y revalidación
 */
export const useUnifiedAdminMetrics = (options: UseUnifiedAdminMetricsOptions = {}) => {
  const queryClient = useQueryClient();
  const { 
    startDate, 
    endDate, 
    staleTime = 15 * 60 * 1000, // 15 minutos por defecto
    cacheTime = 30 * 60 * 1000   // 30 minutos por defecto
  } = options;

  // Función para invalidar el caché y forzar una recarga
  const refreshMetrics = () => {
    return queryClient.invalidateQueries({ queryKey: [UNIFIED_ADMIN_METRICS_KEY] });
  };

  // Consulta principal que obtiene las métricas unificadas
  const query = useQuery({
    queryKey: [UNIFIED_ADMIN_METRICS_KEY, startDate?.toISOString(), endDate?.toISOString()],
    queryFn: async () => {
      console.log('Fetching unified admin metrics...');
      
      // Formatear fechas correctamente para PostgreSQL
      let formattedStartDate = null;
      let formattedEndDate = null;
      
      if (startDate) {
        formattedStartDate = startDate.toISOString();
        console.log('Using start date:', formattedStartDate);
      }
      
      if (endDate) {
        formattedEndDate = endDate.toISOString();
        console.log('Using end date:', formattedEndDate);
      }
      
      try {
        const { data, error } = await supabase.rpc('get_unified_admin_metrics', {
          start_date: formattedStartDate,
          end_date: formattedEndDate
        });

        if (error) {
          console.error('Error fetching unified admin metrics:', error);
          throw error;
        }

        console.log('Unified metrics fetched successfully');
        return data as UnifiedAdminMetrics;
      } catch (error) {
        console.error('Exception in metrics fetch:', error);
        throw error;
      }
    },
    staleTime, // Tiempo antes de considerar los datos obsoletos
    gcTime: cacheTime, // Tiempo para mantener los datos en caché
    refetchOnWindowFocus: false // No recargar automáticamente al enfocar la ventana
  });

  return {
    ...query,
    refreshMetrics
  };
};

// Función auxiliar para obtener métricas históricas
export const fetchHistoricalMetrics = async (days: number = 30) => {
  const { data, error } = await supabase
    .from('admin_metrics_history')
    .select('*')
    .order('metrics_date', { ascending: false })
    .limit(days);

  if (error) {
    console.error('Error fetching historical metrics:', error);
    throw error;
  }

  return data;
};
